import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Spell, WarbandLevelUp } from '../../types';
import { SpellCard } from './SpellCard';
import { SpellCardMini } from './SpellCardMini';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { EmptyStateCard } from './EmptyStateCard';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { SectionHeading } from './SectionHeading';
import { gridFlexSx, miniFlexSx } from '../../App';
import { manageWarbandService } from '../../services/manageWarbandService';
import { computeUnusedLevelUps } from '../../utils/formatting';

const EMPTY_SPELLS: Spell[] = [];


interface OutOfGameSpellListProps {
  categoryIds: string[];
  label: string;
  exclude?: boolean;
  allowDelete?: boolean;
  allowImprove?: boolean;
}

export const CategoryFilteredSpellList: React.FC<OutOfGameSpellListProps> = ({
  categoryIds,
  label,
  exclude = false,
  allowDelete = false,
  allowImprove = false,
}) => {
  const {
    selectedWarbandId,
    spellcastersAndSpells,
    setSpellcastersAndSpells,
    warbands,
    baseResources,
  } = useWarbandStore();
  const warbandSpells = useWarbandStore((s) => s.spellcastersAndSpells?.warbandSpells ?? EMPTY_SPELLS);
  const storeLoading = useWarbandStore((s) => s.isLoading);
  const levelUps = useWarbandStore((s) => s.levelUps);
  const setStoreLevelUps = useWarbandStore((s) => s.setLevelUps);
  const isMinified = useWarbandStore((s) => s.isMinified);
  const queueSpellDelete = useDirtyTrackingStore((s) => s.queueSpellDelete);

  const warband = warbands.find((w) => w.id === selectedWarbandId) ?? null;
  const hasSummoningCircle = baseResources.some((r) => r.id === 'summoning_circle');
  const unusedLevelUps = allowImprove && warband ? computeUnusedLevelUps(warband, levelUps) : 0;

  const spells = useMemo(() => {
    if (!selectedWarbandId) return [];

    const effectiveSpells = hasSummoningCircle
      ? warbandSpells.map((spell) =>
        ['summon_demon', 'control_demon'].includes(spell.id) &&
          !spell.categories.some((cat) => cat.id === 'out_of_game_b')
          ? { ...spell, categories: [...spell.categories, { id: 'out_of_game_b', description: 'Out of Game (B)' }] }
          : spell
      )
      : warbandSpells;

    const filteredSpells = effectiveSpells.filter((spell) => {
      if (exclude) {
        return !spell.categories.every((cat) => categoryIds.includes(cat.id));
      } else {
        return spell.categories.some((cat) => categoryIds.includes(cat.id));
      }
    });
    return [...filteredSpells].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedWarbandId, warbandSpells, categoryIds, exclude, hasSummoningCircle]);

  const handleImproveSpell = useCallback(async (spell: Spell) => {
    if (!selectedWarbandId || !warband) return;
    const totalSlots = Math.max(0, Math.floor(warband.experience / 100));
    const usedLevels = new Set(
      levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned).map((lu) => lu.level)
    );
    let targetLevel: number | null = null;
    for (let level = 2; level <= totalSlots + 1; level++) {
      if (!usedLevels.has(level)) { targetLevel = level; break; }
    }
    if (targetLevel === null) throw new Error('No level-up slot available');

    const newEntry: WarbandLevelUp = {
      warbandId: selectedWarbandId,
      level: targetLevel,
      statsIncrease: null,
      spellImprovement: spell.id,
      spellLearned: null,
    };
    const filledEntries = levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned);
    const updatedLevelUps = [...filledEntries, newEntry];

    // Optimistic update
    const snapshot = spellcastersAndSpells;
    if (snapshot) {
      setSpellcastersAndSpells({
        ...snapshot,
        warbandSpells: snapshot.warbandSpells.map((s) =>
          s.id === spell.id ? { ...s, castingNumber: s.castingNumber - 1 } : s
        ),
      });
    }

    try {
      await manageWarbandService.saveLevelUps(selectedWarbandId, {
        experience: warband.experience,
        levelUps: updatedLevelUps,
      });
      setStoreLevelUps(updatedLevelUps);
    } catch (e) {
      if (snapshot) setSpellcastersAndSpells(snapshot);
      throw e;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarbandId, warband, levelUps, spellcastersAndSpells, setSpellcastersAndSpells, setStoreLevelUps]);

  const [spellPendingDelete, setSpellPendingDelete] = useState<Spell | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDeleteItemRequest = useCallback((spell: Spell) => {
    if (!spell.id || isDeleting) {
      return;
    }
    setSpellPendingDelete(spell);
    setIsDeleteConfirmOpen(true);
  }, [isDeleting]);

  const handleDeleteItemConfirm = useCallback(async () => {
    if (!selectedWarbandId || !spellPendingDelete?.id || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      queueSpellDelete(spellPendingDelete.id);
      setIsDeleteConfirmOpen(false);
      setSpellPendingDelete(null);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedWarbandId, spellPendingDelete, isDeleting, queueSpellDelete]);

  if (!selectedWarbandId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <Typography variant="h6" color="textSecondary">
          No party selected.
        </Typography>
      </Box>
    );
  }

  if (storeLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const boxSx = isMinified ? miniFlexSx : gridFlexSx;


  return (
    <Box>
      <SectionHeading label={label} />
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, p: 1.5 }}>
        {spells.length > 0 ? (
          <Box sx={{ ...boxSx, gap: 3 }}>
            {spells.map((spell) => isMinified ? (
              <SpellCardMini
                spell={spell}
                key={spell.id}
                onDelete={allowDelete ? handleDeleteItemRequest : undefined}
                deleteDisabled={isDeleting}
                unusedLevelUps={allowImprove ? unusedLevelUps : undefined}
                onImprove={allowImprove ? handleImproveSpell : undefined}
              />
            ) : (
              <SpellCard
                spell={spell}
                key={spell.id}
                onDelete={allowDelete ? handleDeleteItemRequest : undefined}
                deleteDisabled={isDeleting}
                unusedLevelUps={allowImprove ? unusedLevelUps : undefined}
                onImprove={allowImprove ? handleImproveSpell : undefined}
              />
            ))}
          </Box>
        ) : (
          <EmptyStateCard message={`Your spellcasters know no ${label}.`} />
        )}
      </Box>

      {allowDelete && (
        <ConfirmActionDialog
          open={isDeleteConfirmOpen}
          title="Confirm Delete"
          message={`Delete ${spellPendingDelete?.name ?? 'this spell'} from this party?`}
          confirmLabel="Delete"
          confirmColor="error"
          busy={isDeleting}
          onClose={() => {
            if (isDeleting) return;
            setIsDeleteConfirmOpen(false);
            setSpellPendingDelete(null);
          }}
          onConfirm={handleDeleteItemConfirm}
        />
      )}
    </Box>
  );
};
